import { IProductResult } from '@api-client/src/api/types';
import roundToDecimalPlaces from '../helpers/roundToDecimalPlaces';

const getWebPImageURL = (imageURL: string): string => {
  return `${imageURL}.webp`;
};

const getBase64ShopifyObjectId = (shopifyObjectName: string, shopifyObjectId: string): string => {
  const buff = Buffer.from(`gid://shopify/${shopifyObjectName}/${shopifyObjectId}`, 'utf-8');
  const base64 = buff.toString('base64');
  return base64;
};

export const getId = (product: IProductResult): string | undefined => {
  return getProductVariantId(product);
};

export const getName = (product: IProductResult): string | undefined => product?.record?.title;

const getImageUrl = (product: any): string => {
  const placeholderImage = `https://cdn.shopify.com/s/files/1/0407/1902/4288/files/placeholder_295x295.jpg?v=1625742127`;

  return product?.record?.product_image || product?.record?.image || placeholderImage;
};

export const getCoverImage = (product: IProductResult, size = 'normal') => {
  let imgResolution = '600x600';
  if (size === 'medium') {
    imgResolution = '295x295';
  } else if (size === 'small') {
    imgResolution = '80x80';
  }

  const placeholderImage = `https://cdn.shopify.com/s/files/1/0407/1902/4288/files/placeholder_${imgResolution}.jpg?v=1625742127`;

  const imgSrc = getImageUrl(product);

  if (imgSrc) {
    const imgPath = imgSrc.substring(0, imgSrc.lastIndexOf('.'));
    const imgext = imgSrc.split('.').pop();
    const resizedImg = getWebPImageURL(`${imgPath}_${imgResolution}.${imgext}`);
    return resizedImg;
  }

  return getWebPImageURL(placeholderImage);
};

export const getPrice = (product: any, options?: { tax: number }): number | null => {
  const tax = options?.tax || 1;
  const price = product?.record?.variants_min_price;

  if (price) {
    const priceFloat = roundToDecimalPlaces(parseFloat(price) * tax, 2);
    if (priceFloat === 0) {
      return null;
    }
    return priceFloat;
  }

  return null;
};

export const hasPrice = (product: IProductResult): boolean => {
  return !!product?.record?.variants_min_price;
};

/*
 * Uses the corresponding (first) compare to price to the variant price
 * returned from getPrice()
 */

export const getCompareToPrice = (product: any, options?: { tax: number }): number | null => {
  const tax = options?.tax || 1;
  const compareToPrice = product?.record?.variants_compare_at_price_min;

  if (compareToPrice) {
    const compareToPriceFloat = roundToDecimalPlaces(parseFloat(compareToPrice) * tax, 2);
    if (compareToPriceFloat === 0) {
      return null;
    }
    return compareToPriceFloat;
  }

  return null;
};

export const hasCompareToPrice = (product: any): boolean => {
  return !!product?.record?.variants_compare_at_price_min;
};

export const getSavingsLabel = (product: IProductResult, options?: { tax: number }): string => {
  const tax = options?.tax || 1;
  const price = getPrice(product, { tax });
  const compareToPrice = getCompareToPrice(product, { tax });

  return hasCompareToPrice(product) &&
    price !== null &&
    compareToPrice !== null &&
    compareToPrice !== undefined &&
    compareToPrice > 0 &&
    compareToPrice > price
    ? `Save ${(100 - (price * 100) / compareToPrice).toFixed(0)}%`
    : '';
};

export const getTags = (product: IProductResult): string[] => {
  const tags = product?.record?.tags.slice() ?? [];

  if (isSpecialOrder(product)) {
    tags.push('special order');
  }

  if (isBackorder(product)) {
    tags.push('backorder');
  }

  return tags;
};

export const getSlug = (product: IProductResult): string | undefined => product?.record?.handle;

export const getProductSKU = (product: IProductResult): string | undefined => product?.record?.sku;

export const getProductPartNumber = (product: IProductResult): string | undefined =>
  product?.record?.meta?.plytix?.part_number;

export const getProductVendor = (product: IProductResult): string | undefined =>
  product?.record?.vendor;

export const getProductId = (product: IProductResult): string | undefined => {
  const productEntry = product?.record['_tags']?.find((item) => item.startsWith('product:'));

  return productEntry?.split(':')[1];
};

export const getProductVariantId = (product: IProductResult): string | undefined => {
  const productEntry = product?.record['_tags']?.find((item) => item.startsWith('variant:'));

  return productEntry?.split(':')[1];
};

export const getBase64ProductId = (product: IProductResult): string => {
  if (product && product.record) {
    const productId = getProductId(product);

    if (productId) {
      return getBase64ShopifyObjectId('Product', productId);
    }
  }
  return '';
};

export const getBase64ProductVariantId = (product: IProductResult): string => {
  const variantId = getProductVariantId(product);

  if (variantId) {
    return getBase64ShopifyObjectId('ProductVariant', variantId);
  }

  return '';
};

export const getQuantity = (
  product: any,
  options: { supportFreeSale?: boolean; includeVariants?: boolean } = {
    supportFreeSale: false,
    includeVariants: false,
  },
): number => {
  const isProductFreeSale = options.supportFreeSale && isFreeSale(product);

  if (isProductFreeSale) {
    return 100;
  }

  if (options.includeVariants) {
    return product.record.variants_inventory_count;
  }

  if (product?.record?.inventory_quantity) {
    return product.record.inventory_quantity;
  }

  return 0;
};

const isMadeToOrder = (product: IProductResult): boolean =>
  isFreeSale(product) && !!product?.record?.meta?.plytix?.made_to_order;

const isFreeSale = (product: IProductResult): boolean => {
  const availableForSale = !!product?.record?.meta?.plytix?.availableForSale;
  const inventoryQty = parseInt(product?.record?.inventory_quantity ?? '0', 10);

  return availableForSale && inventoryQty <= 0;
};

export const isSpecialOrder = (product: IProductResult): boolean => {
  if (product?.record?.meta?.unified_commerce?.special_order_dynamic === true) return true;
  return isFreeSale(product) && !!product?.record?.meta?.plytix?.special_order_required;
};

export const isBackorder = (product: IProductResult): boolean => {
  return isFreeSale(product) && !!product?.record?.meta?.plytix?.backorder_required;
};

export const isInStoreOnly = (product: IProductResult) => {
  return product?.record?.meta?.plytix?.in_store_only || false;
};

export const isInStock = (product: IProductResult): boolean => {
  return product?.record?.meta?.derivedRelevanceAndUi?.inStock || false;
};

export const getStockStatus = (
  product: IProductResult,
): 'in-stock' | 'out-of-stock' | 'made-to-order' => {
  if (isMadeToOrder(product)) {
    return 'made-to-order';
  }

  if (isInStock(product)) {
    return 'in-stock';
  }

  return 'out-of-stock';
};

export const hasVariants = (product: IProductResult): boolean => {
  if (product?.record?.meta?.bundles?.type) {
    return product?.record?.meta?.bundles?.type === 'variant';
  }

  return product?.record?.meta?.plytix?.has_variants || false;
};

export const isUniversal = (product: IProductResult) => !!product?.record?.meta?.fitment?.universal;

export const hasVehicles = (product: IProductResult): boolean =>
  !!product?.record?.meta?.fitment?.has_vehicles;

export const hasFitment = (product: IProductResult): boolean =>
  !(!hasVehicles(product) && !isUniversal(product));

export const fitsVehicle = (product: IProductResult, vehicleId: number | undefined): boolean =>
  !!(vehicleId && product?.record?.meta?.fitment?.vehicles?.includes(vehicleId));

/**
 * Finds the max prices for the product.
 *
 * max_price is not used since it is incorrect (a Search.io bug).
 * The logic is found in collaboration with the product (Doug, see SPARK-5289).
 */
export const getMaxPrice = (product: IProductResult): number | null => {
  const variantsPrices = getVariantsPrices(product);
  const variantsCompareAtPrices = getVariantsCompareAtPrices(product);

  const allPricesCombined = [...variantsPrices, ...variantsCompareAtPrices];
  const maxPrice = Math.max(...allPricesCombined);

  return isFinite(maxPrice) ? maxPrice : null;
};

export const getMinPrice = (product: IProductResult): number => {
  return product?.record?.variants_min_price ?? 0;
};

export const getObjectID = (product: IProductResult): string => {
  return product?.record?.objectID || '';
};

function getVariantsPrices(product: IProductResult): number[] {
  const out = [];

  if (product.record.variants_min_price) {
    out.push(product.record.variants_min_price);
  }

  if (product.record.variants_max_price) {
    out.push(product.record.variants_max_price);
  }

  return out;
}

function getVariantsCompareAtPrices(product: IProductResult): number[] {
  const out = [];

  if (product.record.variants_compare_at_price_min) {
    out.push(product.record.variants_compare_at_price_min);
  }

  if (product.record.variants_compare_at_price_max) {
    out.push(product.record.variants_compare_at_price_max);
  }

  return out;
}
